import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { plainToInstance } from "class-transformer";
import { Observable, of } from "rxjs";
import { map, startWith, switchMap } from 'rxjs/operators';


import { CacheService } from "../../services/cache.service";
import { PusherService } from "../../services/pusher.service";
import { buildFilters } from "../../utilities/filters";
import { SlotsRequest } from "./../models/slots-request.model";

@Injectable({
  providedIn: 'root'
})
export class SlotRequestService {

  constructor(
    private http: HttpClient,
    private cacheService: CacheService,
    private pusherService: PusherService
  ) { }

  private baseUrl = '/:apiBase/companies/:companyId';
  private slotRequest = this.baseUrl + '/slots-request';
  private slotRequestSummary = this.slotRequest + '/summary';
  private slotRequestReject = this.slotRequest + '/:slotRequestId/reject';
  private slotRequestRetire = this.slotRequest + '/:slotRequestId/retire';
  private slotRequestById = this.slotRequest + '/:slotRequestId';
  private slotRequestExportUrl = '/:apiSlotRequests/companies/:companyId/slot-requests/export';

  private readonly CACHE_MINUTES = 10;
  /**
   * Maps only those parameters that don't match in the API call.
   * Format - 'Webapp query': 'API query'
   */
  private readonly queryMap: Record<string, string> = {
    'product_id': 'filters[product][id]',
    'validity': 'filters[validity]',
    'negotiation_id': 'filters[negotiation][reference]',
    'recipient_name': 'filters[recipient_name]',
    'assigned_to': 'filters[assigned_to]',
    'sustainable': 'filters[sustainable]',
    'slot_request_status': 'filters[status_id]',
    'slot_request_active': 'filters[active]',
    'slot_request_date': 'filters[date]',
    'zone_id': 'filters[zone_id]',
  };

  public summary(companyId: number, filters?: any): Observable<{ pending_count: number, pending_quantity: number }> {
    let url = this.slotRequestSummary.replace(':companyId', companyId.toString());
    url = buildFilters(url, filters, this.queryMap);
    return this.http.get<{ pending_count: number, pending_quantity: number }>(url);
  }

  public create(companyId: number, data: SlotsRequest[]): Observable<any> {
    const url = this.slotRequest
      .replace(':companyId', companyId.toString());

    return this.http.post(url, data);
  }

  public get(companyId: number, filters?: any, paginated: boolean = true): Observable<{ body: SlotsRequest[], headers: HttpHeaders }> {
    if (paginated && !filters?.page) filters = { ...filters, page: 1 };

    // Normaliza el filtro de rango de fecha para que el backend reciba el formato esperado (between:from,to)
    if (filters?.slot_request_date && typeof filters.slot_request_date === 'string' && filters.slot_request_date.startsWith('range:')) {
      filters = {
        ...filters,
        slot_request_date: 'between:' + filters.slot_request_date.slice(6)
      };
    }

    let url = this.slotRequest.replace(':companyId', companyId.toString());
    url = buildFilters(url, filters, this.queryMap);

    const cached = this.cacheService.get(url);
    // Return cached value if exists
    if (cached) {
      // Reconstruct HttpHeaders from plain key-value pairs (survives localStorage serialization)
      const restoredHeaders = new HttpHeaders(cached.headers as Record<string, string>);
      return of({ body: plainToInstance<SlotsRequest, SlotsRequest[]>(SlotsRequest, cached.body), headers: restoredHeaders });
    }

    const stream = this.http.get<SlotsRequest[]>(url, { observe: 'response' });
    return stream.pipe(map(response => {
      // Store pagination headers as plain strings so they survive JSON serialization in localStorage
      const paginationHeaders: Record<string, string> = {};
      [
        'X-Total-Count', 'X-Per-Page', 'X-Current-Page',
        'X-Total-Pages', 'X-Item-Range-From', 'X-Item-Range-To', 'X-Subitem-Total-Count'
      ].forEach(name => {
        const value = response.headers.get(name);
        if (value !== null) paginationHeaders[name] = value;
      });
      this.cacheService.set(url, { body: response.body, headers: paginationHeaders }, this.CACHE_MINUTES);
      return { body: plainToInstance(SlotsRequest, response.body), headers: response.headers };
    }));

  }

  public reject(companyId: number, slotsRequestId: number, observations: string = ""): Observable<SlotsRequest> {
    const url = this.slotRequestReject
      .replace(':companyId', String(companyId))
      .replace(':slotRequestId', String(slotsRequestId))

    return this.http.patch<SlotsRequest>(url, { observations });
  }

  public retire(companyId: number, slotsRequestId: number, observations?: string): Observable<SlotsRequest> {
    const url = this.slotRequestRetire
      .replace(':companyId', String(companyId))
      .replace(':slotRequestId', String(slotsRequestId))

    return this.http.patch<SlotsRequest>(url, { observations });
  }

  public update(companyId: number, slotsRequestId: number, data: { quantity: number, date: Date, observations?: string }): Observable<SlotsRequest> {
    const url = this.slotRequestById
      .replace(':companyId', String(companyId))
      .replace(':slotRequestId', String(slotsRequestId));

    return this.http.patch<SlotsRequest>(url, data);
  }

  public delete(companyId: number, slotsRequestId: number, observations?: string): Observable<any> {
    const url = this.slotRequestById
      .replace(':companyId', String(companyId))
      .replace(':slotRequestId', String(slotsRequestId))

    return this.http.delete(url, { body: observations });
  }

  public export(companyId: number, filters?: any): Observable<any> {
    let url = this.slotRequestExportUrl.replace(':companyId', String(companyId));
    url = buildFilters(url, filters, this.queryMap);
    return this.http.post(url, {}, { observe: 'response' });
  }

  public watch(companyId: number, userId: number, filters?: any, paginated?: boolean): Observable<{ body: SlotsRequest[], headers: HttpHeaders }> {
    return this.pusherService.listenToMultiple([
      { channel: `company_${companyId}`, event: 'slot_request' },
      { channel: `private_${userId}`, event: 'slot_request' }
    ], 2000).pipe(
      startWith({}),
      // Mapea cada evento a una nueva llamada a `get`
      switchMap((event, index) => {
        // Limpiar caché solo cuando hay un evento real (no en el startWith inicial)
        if (index > 0) {
          const url = this.slotRequest.replace(':companyId', companyId.toString());
          this.cacheService.clear(url);
        }
        return this.get(companyId, filters, paginated);
      })
    );
  }
}